import { Chart, registerables } from 'chart.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { ChartLayout, OverviewContent, RelevanceContent, EquityContent, MethodologyContent } from './ChartLayout';
import './ChartLayout.css';

Chart.register(...registerables);

interface Props {
  location: string;              // Display name for chart subtitles/filenames
  geofips: number | null;        // County geofips to filter county series; if null, chart will show only state if provided
  preferredState?: string | null; // Optional: force a specific state name (e.g., 'California'); otherwise inferred from county rows
  yearStart?: number;
  yearEnd?: number;
}

type Row = {
  geofips: number;
  location_name: string;
  county: string | null;
  state: string;
  geography_type: 'state' | 'county' | string;
  year: number;
  percent_drove_alone: number; // assumed 0–100; if 0–1, adjust scale below
  margin_of_error: number | null;
};

export const PercentDroveAloneWork: React.FC<Props> = ({
  location,
  geofips,
  preferredState = null,
  yearStart = 2010,
  yearEnd = 2023
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countyRows, setCountyRows] = useState<Row[]>([]);
  const [stateRows, setStateRows] = useState<Row[]>([]);
  const [activeView, setActiveView] = useState<string>('overview');

  const years = useMemo(
    () => Array.from({ length: (yearEnd - yearStart + 1) }, (_, i) => yearStart + i),
    [yearStart, yearEnd]
  );

  useEffect(() => {
    const fetchData = async () => {
      // Add early return if location is not set
      if (!location || location === '') {
        console.log('No location set yet, waiting...');
        setLoading(false);
        return;
      }
      
      if (!geofips && !preferredState) {
        setError('A county geofips or preferredState is required to render this chart.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // 1) County series - Match the pattern from other working charts
        let countyStateName: string | null = null;
        if (geofips) {
          console.log('Fetching percent_drove_alone_work data for geofips:', geofips, 'location:', location);
          
          // Direct geofips query - matching pattern from HeatPumpUnitsChart and efrancktestchart
          const { data: cData, error: cErr } = await supabaseClient
            .from('percent_drove_alone_work')
            .select('*')
            .eq('geofips', geofips)
            .eq('geography_type', 'county')
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });

          if (cErr) {
            console.error('Error fetching county data:', cErr);
            throw cErr;
          }

          console.log('County data found:', cData?.length || 0, 'rows');
          if (cData && cData.length > 0) {
            console.log('Sample county data:', cData[0]);
          } else {
            console.warn(`No commuting data found for geofips ${geofips} (${location})`);
          }
          
          setCountyRows(cData || []);
          countyStateName = (cData && cData[0]?.state) || null;
        } else {
          console.log('No geofips provided, skipping county data');
          setCountyRows([]);
        }

        // 2) State series (prefer explicit prop; else infer from county)
        const stateName = preferredState || countyStateName;
        if (stateName) {
          console.log('Fetching state data for:', stateName);
          
          // Simple state data fetch
          const { data: sData, error: sErr } = await supabaseClient
            .from('percent_drove_alone_work')
            .select('*')
            .eq('geography_type', 'state')
            .eq('state', stateName)
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });

          if (sErr) {
            console.error('State data error:', sErr);
            setStateRows([]);
          } else {
            console.log('State data found:', sData?.length || 0, 'rows');
            console.log('Sample state data:', sData?.[0]);
            setStateRows(sData || []);
          }
        } else {
          console.log('No state name available, skipping state data');
          setStateRows([]);
        }

        setLoading(false);
      } catch (e: any) {
        console.error('Error loading percent_drove_alone_work:', e);
        setError(e.message || 'Failed to load percent_drove_alone_work');
        setLoading(false);
      }
    };

    fetchData();
  }, [geofips, preferredState, yearStart, yearEnd, location]);

  // Prepare data series with useMemo to avoid dependency issues
  const countySeries = useMemo(() => {
    // Build a map of year -> value to fill gaps with nulls
    const byYear = new Map<number, Row>();
    countyRows.forEach(r => byYear.set(r.year, r));
    const values = years.map(y => {
      const r = byYear.get(y);
      if (!r) return null;
      return (r.percent_drove_alone ?? null);
    });
    const moe = years.map(y => {
      const r = byYear.get(y);
      return r?.margin_of_error ?? null;
    });
    return { values, moe };
  }, [countyRows, years]);

  const stateSeries = useMemo(() => {
    // Build a map of year -> value to fill gaps with nulls
    const byYear = new Map<number, Row>();
    stateRows.forEach(r => byYear.set(r.year, r));
    const values = years.map(y => {
      const r = byYear.get(y);
      if (!r) return null;
      return (r.percent_drove_alone ?? null);
    });
    const moe = years.map(y => {
      const r = byYear.get(y);
      return r?.margin_of_error ?? null;
    });
    return { values, moe };
  }, [stateRows, years]);

  // Calculate key metrics
  const keyMetrics = useMemo(() => {
    if (loading || (!countyRows.length && !stateRows.length)) return null;
    
    const allData = [...countyRows, ...stateRows];
    const validData = allData.filter(row => row.percent_drove_alone != null);
    
    if (!validData.length) return null;
    
    const latestYear = Math.max(...validData.map(row => row.year));
    const latestData = validData.filter(row => row.year === latestYear);
    const avgLatest = latestData.reduce((sum, row) => sum + row.percent_drove_alone, 0) / latestData.length;
    
    return {
      totalDataPoints: validData.length,
      latestYear: latestYear,
      averageLatest: avgLatest,
      dateRange: `${Math.min(...validData.map(row => row.year))}-${Math.max(...validData.map(row => row.year))}`
    };
  }, [loading, countyRows, stateRows]);

  // Build the chart after data loads
  useEffect(() => {
    if (loading) return;

    const ctx = chartRef.current?.getContext('2d');
    if (!ctx) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const datasets: any[] = [];

    if (stateRows.length > 0) {
      datasets.push({
        label: stateRows[0].state || 'State',
        data: stateSeries.values,
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderWidth: 2,
        spanGaps: true,
        tension: 0.25,
        pointRadius: 3
      });
    }

    if (countyRows.length > 0) {
      const countyLabel = countyRows[0].county
        ? `${countyRows[0].county}, ${countyRows[0].state}`
        : countyRows[0].location_name || 'County';
      datasets.push({
        label: countyLabel,
        data: countySeries.values,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.2)',
        borderWidth: 2,
        spanGaps: true,
        tension: 0.25,
        pointRadius: 3
      });
    }

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'nearest', intersect: false },
        scales: {
          y: {
            title: { display: true, text: '% Drove Alone to Work' },
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              callback: (v: any) => `${v}%`
            }
          },
          x: {
            title: { display: true, text: 'Year' }
          }
        },
        plugins: {
          legend: {
            position: 'bottom'
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y;
                return `${ctx.dataset.label}: ${val != null ? val.toFixed(1) : 'N/A'}%`;
              }
            }
          }
        }
      }
    });
  }, [loading, years, countyRows, stateRows, countySeries.values, stateSeries.values]);

  const handleDownloadPNG = () => {
    const filename = formatFilename(location, 'Percent_Drove_Alone_Work');
    downloadChartAsPNG(chartRef, filename);
  };

  const handleDownloadCSV = () => {
    // Flatten to tidy rows for download
    const rows: Array<Record<string, any>> = [];
    years.forEach((y, i) => {
      rows.push({
        year: y,
        state: stateRows[0]?.state ?? null,
        state_percent: stateSeries.values[i],
        state_moe: stateSeries.moe[i],
        county: countyRows[0]?.county ?? null,
        county_state: countyRows[0]?.state ?? null,
        county_percent: countySeries.values[i],
        county_moe: countySeries.moe[i]
      });
    });
    downloadDataAsCSV(rows, formatFilename(location, 'Percent_Drove_Alone_Work_Data'));
  };

  if (loading) {
    return (
      <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading percent drove alone data for {location || 'selected location'}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ color: '#ef4444', marginBottom: '10px' }}>{error}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Location: {location || 'Not set'}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Geofips: {geofips || 'Not set'}</div>
      </div>
    );
  }
  
  // If no location is set or no data found, don't show anything
  if (!location || (!loading && countyRows.length === 0 && stateRows.length === 0)) {
    return null;
  }

  const chartContent = (
    <div style={{ height: 400, width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  // Key metrics display
  const keyMetricsDisplay = keyMetrics ? (
    <div className="key-metrics-container">
      <div className="key-metric-item">
        <div className="key-metric-value">{keyMetrics.totalDataPoints}</div>
        <div className="key-metric-label">Data Points</div>
      </div>
      <div className="key-metric-item">
        <div className="key-metric-value">{keyMetrics.averageLatest.toFixed(1)}%</div>
        <div className="key-metric-label">Average Latest Year</div>
      </div>
      <div className="key-metric-item">
        <div className="key-metric-value">{keyMetrics.dateRange}</div>
        <div className="key-metric-label">Date Range</div>
      </div>
    </div>
  ) : null;

  return (
    <ChartLayout
      title="Percent Drove Alone to Work"
      description={location}
      chartType="line"
      keyMetrics={keyMetricsDisplay}
      chartContent={chartContent}
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      layout="side-by-side"
      onDownloadPNG={handleDownloadPNG}
      onDownloadCSV={handleDownloadCSV}
      pngFilename={formatFilename(location, 'Percent_Drove_Alone_Work')}
      csvFilename={formatFilename(location, 'Percent_Drove_Alone_Work_Data')}
      overviewContent={
        <OverviewContent
          title="Commuting Patterns Analysis"
          description="This chart shows the percentage of workers who drove alone to work over time, comparing county-level data with state averages. Understanding commuting patterns is crucial for transportation planning and emissions reduction strategies."
        >
          <div>
            <p><strong>Key Insights:</strong></p>
            <ul>
              <li>Drove alone rates vary significantly between counties and the state average</li>
              <li>Trends can indicate changes in transportation infrastructure, remote work adoption, or economic factors</li>
              <li>Higher rates may suggest need for improved public transportation or alternative commuting options</li>
              <li>Data includes margin of error estimates for statistical reliability</li>
            </ul>
          </div>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Transportation and Climate Impact"
          description="Commuting patterns directly impact greenhouse gas emissions and urban planning decisions."
        >
          <div>
            <p><strong>Climate Relevance:</strong></p>
            <ul>
              <li>Single-occupancy vehicles contribute significantly to transportation emissions</li>
              <li>Understanding current patterns helps identify opportunities for emissions reduction</li>
              <li>Data supports transportation planning for sustainable mobility solutions</li>
              <li>Trends can inform policy decisions on public transit investment and infrastructure</li>
            </ul>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Transportation Equity Considerations"
          description="Commuting patterns reflect and impact transportation equity across different communities."
        >
          <div>
            <p><strong>Equity Implications:</strong></p>
            <ul>
              <li>Higher drove alone rates may indicate limited access to public transportation</li>
              <li>Transportation options vary by income, location, and demographic factors</li>
              <li>Data can help identify underserved communities needing better transit access</li>
              <li>Understanding patterns supports equitable transportation investment decisions</li>
            </ul>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Data Sources and Methodology"
          description="This analysis uses American Community Survey data to examine commuting patterns over time."
          dataSources={[
            'American Community Survey (ACS) 5-Year Estimates',
            'U.S. Census Bureau',
            'Table: S0801 - Means of Transportation to Work by Selected Characteristics'
          ]}
          methodology={[
            'Data extracted from ACS 5-year estimates for county and state levels',
            'Percent drove alone calculated as (workers driving alone / total workers) × 100',
            'Margin of error provided for statistical confidence intervals',
            'Time series analysis from 2010-2023 where data available',
            'County data filtered by geographic identifier (GEOID)',
            'State-level data for comparison and context'
          ]}
        />
      }
    />
  );
};

export default PercentDroveAloneWork;
